import { z } from 'zod';
import { Money } from './money';

export const LoyaltyTier = z.object({
  from: Money.min(0),
  percent: z.number().int().min(1).max(50),
});
export const LoyaltySettings = z.array(LoyaltyTier).max(5);
export type LoyaltySettings = z.infer<typeof LoyaltySettings>;

/** Касса ищет/заводит гостя по телефону одним запросом. */
export const GuestLookupRequest = z.object({
  phone: z.string().min(5).max(20),
  name: z.string().min(1).max(60).optional(),
});
export type GuestLookupRequest = z.infer<typeof GuestLookupRequest>;

export const GuestLookupResponse = z.object({
  guestId: z.string(),
  phone: z.string(),        // каноническая форма 7XXXXXXXXXX
  name: z.string().nullable(),
  /** заметка кассира: «аллергия на орехи» — видна при привязке и оплате */
  note: z.string().nullable(),
  totalSpent: Money,
  visits: z.number().int(),
  percent: z.number().int(), // текущий процент лояльности
  /** последние чеки — кассир видит, что гость реально свой */
  recentOrders: z.array(z.object({
    at: z.string().datetime(),
    total: Money,
  })).max(5),
  /** позиции последнего чека — для «повторить заказ» одной кнопкой */
  lastOrderItems: z.array(z.object({
    productId: z.string().nullable(),
    name: z.string(),
    qty: z.number(),
  })).max(50),
  /** топ-позиции гостя — кассир может предложить «как обычно?» */
  favorites: z.array(z.object({ name: z.string(), qty: z.number() })).max(3),
});
export type GuestLookupResponse = z.infer<typeof GuestLookupResponse>;

export const GuestRow = z.object({
  guestId: z.string(),
  phone: z.string(),
  name: z.string().nullable(),
  totalSpent: Money,
  visits: z.number().int(),
  lastVisit: z.string().datetime().nullable(),
  percent: z.number().int(),
  /** первый визит — «новые за период» считаются на клиенте */
  firstSeen: z.string().datetime().optional(),
});
export const GuestsList = z.object({ rows: z.array(GuestRow) });
export type GuestsList = z.infer<typeof GuestsList>;

export const PatchGuest = z.object({
  name: z.string().min(1).max(60).optional(),
  note: z.string().max(200).nullable().optional(),
}).refine((v) => v.name !== undefined || v.note !== undefined, { message: 'empty patch' });
export type PatchGuest = z.infer<typeof PatchGuest>;

/** «Приведи друга»: после каждых everyVisits визитов гость получает
 *  на чеке разовый код для друга. */
export const ReferralConfig = z.object({
  everyVisits: z.number().int().min(2).max(50),
  percent: z.number().int().min(1).max(50),
}).nullable();
export type ReferralConfig = z.infer<typeof ReferralConfig>;

export const RedeemCodeRequest = z.object({ code: z.string().min(4).max(12) });
export const RedeemCodeResponse = z.object({ percent: z.number().int() });
export type RedeemCodeResponse = z.infer<typeof RedeemCodeResponse>;

/** Промокоды за период: газетная строка для владельца. */
export const ReferralStats = z.object({
  issued: z.number().int(),
  redeemed: z.number().int(),
  expired: z.number().int(),
  discountGiven: Money,
  revenueFromRedeemed: Money,
});
export type ReferralStats = z.infer<typeof ReferralStats>;

/** Топ гостей за 30 дней — кого благодарить лично. */
export const TopGuests = z.object({
  rows: z.array(z.object({
    name: z.string().nullable(),
    phone: z.string(),
    sum: Money,
    checks: z.number().int(),
  })),
});
export type TopGuests = z.infer<typeof TopGuests>;
